// ST-1258 + ST-1275 -Claude- Signup page — first/last name + email + password.
import { useState, type FormEvent, type ChangeEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Card } from '@nearrme/ui-web';
import { signup } from '@nearrme/api-client';
import { getApiClient } from '../api/client';
import { setConsumerToken } from '../auth/session';

export function SignupPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromConsumerLogin = searchParams.get('source') === 'consumer-required';
  const [form, setForm] = useState({
    firstName: searchParams.get('firstName') ?? '',
    lastName: searchParams.get('lastName') ?? '',
    email: searchParams.get('email') ?? '',
    password: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const update = (k: keyof typeof form) => (e: ChangeEvent<HTMLInputElement>) =>
    setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await signup(getApiClient(), form);
      if (res.data?.token) {
        setConsumerToken(res.data.token);
        navigate('/');
      } else {
        setError(res.message ?? t('errors.generic'));
      }
    } catch (err: any) {
      console.error('[signup failed]', err);
      setError(err?.response?.data?.message ?? t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-10 sm:py-14 animate-fade-in">
      <div className="grid lg:grid-cols-[minmax(0,1fr)_420px] gap-8 items-stretch">
        <section className="hidden lg:flex lg:min-h-[560px] rounded-xl overflow-hidden bg-gradient-to-br from-brand-700 via-emerald-700 to-slate-950 text-white p-8 flex-col justify-between">
          <div>
            <p className="text-sm font-semibold uppercase tracking-wide text-emerald-100">Nearrme account</p>
            <h1 className="mt-4 text-4xl font-bold tracking-tight">
              Find trusted stores before you buy.
            </h1>
            <p className="mt-4 text-lg text-emerald-50">
              Save nearby products, reserve at verified stores, and keep warranty-backed purchases in one place.
            </p>
          </div>
          <div className="grid gap-3">
            {[
              'Manufacturer verified stores',
              'Original manufacturer warranty',
              'Nearby inventory before you travel',
            ].map((item) => (
              <div key={item} className="rounded-lg bg-white/10 border border-white/15 px-4 py-3 text-sm font-medium">
                {item}
              </div>
            ))}
          </div>
        </section>

        <Card padded={true} className="w-full lg:min-h-[560px]">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 text-center">
          {t('auth.signupTitle')}
        </h1>
        {fromConsumerLogin && (
          <div className="mt-4 rounded-lg border border-brand-200 bg-brand-50 px-3 py-3 text-sm text-brand-900 dark:border-brand-800 dark:bg-brand-950 dark:text-brand-100">
            Create a consumer account for this email to save favorites, reserve products, and manage Nearrme purchases.
          </div>
        )}

        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                {t('auth.firstName')}
              </label>
              <input
                type="text"
                required
                value={form.firstName}
                onChange={update('firstName')}
                className="w-full h-10 px-3 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                {t('auth.lastName')}
              </label>
              <input
                type="text"
                required
                value={form.lastName}
                onChange={update('lastName')}
                className="w-full h-10 px-3 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              {t('auth.email')}
            </label>
            <input
              type="email"
              required
              value={form.email}
              onChange={update('email')}
              className="w-full h-10 px-3 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              {t('auth.password')}
            </label>
            <input
              type="password"
              required
              minLength={8}
              value={form.password}
              onChange={update('password')}
              className="w-full h-10 px-3 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              At least 8 characters with 1 upper, 1 lower, 1 digit.
            </p>
          </div>

          {error && (
            <div className="text-sm text-red-500 bg-red-50 dark:bg-red-950 px-3 py-2 rounded-lg">
              {error}
            </div>
          )}

          <Button type="submit" variant="primary" size="lg" fullWidth loading={loading}>
            {t('auth.signupButton')}
          </Button>
        </form>

        <div className="mt-4 text-center text-sm">
          <Link to="/login" className="text-brand-700 dark:text-brand-300 hover:underline font-medium">
            {t('auth.loginTitle')}
          </Link>
        </div>
        <div className="mt-3 text-center text-sm">
          <Link to="/business/signup" className="text-slate-600 dark:text-slate-300 hover:text-brand-700">
            Signing up a business?
          </Link>
        </div>
        </Card>
      </div>
    </div>
  );
}
